import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation, type GeoSearchResultItem } from '@smg-real-estate/geo-libs';

// TODO Move to a library so that both seeker-app and entry-page share same logic

export interface HighlightSegment {
  text: string;
  bold: boolean;
}

export function getHighlightedDisplaySegments(
  geoSearchResultItem: GeoSearchResultItem,
  language: HgSupportedLanguage,
): HighlightSegment[] {
  const nameSegments = geoSearchResultItem.geoLocation.names[language].map((name) =>
    getNameSegments(name, geoSearchResultItem.highlights),
  );

  return joinDisplaySegments(nameSegments);
}

function getNameSegments(
  name: string,
  highlights: GeoSearchResultItem['highlights'],
): HighlightSegment[] {
  const segments: HighlightSegment[] = [];

  if (!Object.hasOwnProperty.call(highlights, name)) {
    pushSegment(segments, name, false);
    return segments;
  }

  let i = 0;

  for (const { from, to } of highlights[name]) {
    if (isInvalidRange(name, from, to)) {
      continue;
    }

    pushSegment(segments, name.substring(i, from), false);
    pushSegment(segments, name.substring(from, to), true);
    i = to;
  }

  pushSegment(segments, name.substring(i), false);
  return segments;
}

/** Mirrors getDisplayValue join rules: first (rest joined by ", ") */
function joinDisplaySegments(nameSegments: HighlightSegment[][]): HighlightSegment[] {
  const [first, ...rest] = nameSegments;

  if (!first || first.every((segment) => !segment.text)) {
    return [];
  }

  const result = [...first];

  if (rest.length === 0) {
    return result;
  }

  pushSegment(result, ' (', false);

  rest.forEach((segments, index) => {
    if (index > 0) {
      pushSegment(result, ', ', false);
    }
    result.push(...segments);
  });

  pushSegment(result, ')', false);
  return result;
}

function pushSegment(segments: HighlightSegment[], text: string, bold: boolean): void {
  if (text === '') {
    return;
  }
  segments.push({ text, bold });
}

const isInvalidRange = (term: string, from: number, to: number): boolean =>
  from < 0 || from > term.length || to < 0 || to > term.length;

const showTypeLabel = (location: GeoLocation) => ['canton', 'region'].includes(location.type);

export function renderGeoLocationName(
  location: GeoLocation,
  language: HgSupportedLanguage,
): string {
  const [first, ...rest] = location.names[language];

  return [
    showTypeLabel(location) ? location.typeLabel?.[language] : undefined,
    rest.length > 0 ? `${first} (${rest.join(', ')})` : first,
  ]
    .filter((x) => !!x)
    .join(' ');
}
